import "./Select.scss"

import React, { FunctionComponent, useState, useMemo, useRef, KeyboardEvent } from "react"
import { useTranslation } from "react-i18next"
import { AsyncPaginate, reduceGroupedOptions } from "react-select-async-paginate"
import { MenuPlacement, MenuPosition } from "react-select"

import iconClearIndicator from "assets/img/icon-clear-indicator.svg"

import { GroupedControl } from "config/constants"
import { IMultipleEntities } from "@/types"
import SelectComponents, { INoOptionsMessageConfigJson } from "./SelectComponents"
import getSelectStyles from "./SelectStyles"
import CustomError from "models/CustomError"
import SelectCreateLabel from "./SelectCreateLabel"
import useClickOutside from "hooks/useClickOutside"
import useSearchSession from "./useSearchSession"
import Highlighter from "components/Highlighter/Highlighter"
import HelpButton, { IHelpButtonProps } from "components/HelpButton/HelpButton"
import { getZIndex } from "utils/modal"
import { CreatableAsyncPaginate } from "./Select"

const rootSelectMenuPortal = document.getElementById("root-select-menu-portal") as HTMLElement

/**
 * Marks a carrier object that wraps a primitive option value.
 *
 * react-select 5 detects option groups with `'options' in option`, which throws a
 * `TypeError` when an option is a string/number primitive (v4 used a property read instead).
 * SelectSearch is used with raw string options (e.g. customer-name autocomplete), so primitives
 * are wrapped in `{ label, value }` carriers before reaching react-select and unwrapped again
 * via {@link fromSelectOption} so consumer templates / onChange keep receiving the raw item.
 */
const RAW_OPTION = Symbol("rawSelectSearchOption")

function toSelectOption(item: any) {
    if (item !== null && typeof item === "object") return item
    return { label: String(item), value: item, [RAW_OPTION]: item }
}

function fromSelectOption(option: any) {
    return option && typeof option === "object" && RAW_OPTION in option ? option[RAW_OPTION] : option
}

export interface ISelectOption<T = any, D = any> {
    label: string,
    value: T,
    data?: D,
    isDisabled?: boolean
}

export interface ISelectProps {
    placeholder?: string
    onChange?(item: any): void
    onAdd?(newValue: string): void
    getOptions(search?: string): Promise<(IMultipleEntities<any> | null)[]>
    optionTemplate?(option: any, search: string): React.JSX.Element | string
    valueTemplate?(option: any, search: string): React.JSX.Element | string
    isCreatable?: boolean
    grouped?: GroupedControl
    helpTooltip?: IHelpButtonProps["tooltipProps"]
    noOptionsMessage?: React.JSX.Element
    menuWidth?: string
    menuAlign?: "left" | "right"
    dropdownIndicator?: boolean
    forceKeepMenuOpen?: boolean
    blurInputOnSelect?: boolean
    onCreateArticleClick?(): void
    testIdPrefix?: string
    isInModal?: boolean
    shouldShowUncontrolledValue?: boolean,
    isInvalid?: boolean,
    isDisabled?: boolean,
    keepValueOnSelect?: boolean
    onMenuOpen?: () => void
    onMenuClose?: () => void
    onClear?: () => void
    selectedValue?: any
    extraCacheUniqs?: any[]
    defaultOptions?: boolean
    loadOptionsOnMenuOpen?: boolean
    debounceTimeout?: number
    searchOnlyWithCriteria?: boolean
    onSearchPendingChange?: (isPending: boolean) => void
}

const SelectSearch: FunctionComponent<React.PropsWithChildren<ISelectProps>> = ({
    placeholder = "",
    getOptions,
    onChange = () => { },
    onAdd = () => { },
    optionTemplate,
    valueTemplate,
    isCreatable,
    grouped,
    helpTooltip,
    menuWidth,
    menuAlign,
    dropdownIndicator,
    forceKeepMenuOpen,
    blurInputOnSelect = true,
    onCreateArticleClick,
    testIdPrefix = "select",
    isInModal,
    shouldShowUncontrolledValue,
    isInvalid,
    isDisabled,
    keepValueOnSelect,
    onMenuOpen: handleMenuOpen,
    onMenuClose: handleMenuClose,
    onClear,
    selectedValue,
    extraCacheUniqs,
    defaultOptions,
    loadOptionsOnMenuOpen,
    debounceTimeout = 500,
    searchOnlyWithCriteria,
    onSearchPendingChange,
}) => {
    const { t } = useTranslation()
    const [value, setValueInternal] = useState<any>(selectedValue ?? "")
    const [options, setOptions] = useState<any[]>([])
    const [currentSearch, setCurrentSearch] = useState<string | null>("")
    const [loadMoreCalls, setLoadMoreCalls] = useState<(() => Promise<IMultipleEntities<any> | null>)[]>([])
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const session = useSearchSession({ enabled: searchOnlyWithCriteria, onSearchPendingChange })
    const wrapperRef = useRef<HTMLDivElement>(null)
    const suppressMenuCloseRef = useRef(false)
    const menuZIndex = useMemo(() => isInModal ? getZIndex() : undefined, [isInModal])
    const [hasMore, setHasMore] = useState(false)

    React.useEffect(() => {
        if (selectedValue !== undefined) {
            setValueInternal(selectedValue)
        } else {
            setValueInternal("")
        }
    }, [selectedValue])


    React.useEffect(() => {
        setLoadMoreCalls([])
        setCurrentSearch(null)
        setOptions([])
        setHasMore(false)
        session.handleCacheKeysChanged()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, extraCacheUniqs || [])

    useClickOutside(closeMenu, wrapperRef.current)

    const hasValue = useMemo(() => {
        return !!value
    }, [value])

    async function loadOptions(search: string = "") {
        setErrorMessage(null)

        if (session.shouldSkipLoad) {
            setOptions([])
            setLoadMoreCalls([])
            setCurrentSearch(search)
            setHasMore(false)
            session.notifySkippedLoad()
            return {
                options: [],
                hasMore: false
            }
        }

        session.notifyLoadStart()
        try {
            const hasLoadMoreCalls = loadMoreCalls.length > 0
            const isSameSearch = search === currentSearch

            let resp: (IMultipleEntities<any> | null)[]
            let isFirstPage = true

            try {
                if (isSameSearch && hasLoadMoreCalls) {
                    resp = await Promise.all(loadMoreCalls.map(call => call()))
                    isFirstPage = false
                } else {
                    resp = await getOptions(search)
                }
            } catch (e) {
                if (e instanceof CustomError) {
                    setErrorMessage(e.message)
                }
                setOptions([])
                setCurrentSearch(search)
                setLoadMoreCalls([])
                setHasMore(false)
                return {
                    options: [],
                    hasMore: false
                }
            }

            let foundItems: any[] = []
            const newLoadMoreCalls: (() => Promise<IMultipleEntities<any> | null>)[] = []
            for (let respPartial of resp) {
                if (!respPartial) {
                    continue
                }
                if (respPartial.loadMore) {
                    newLoadMoreCalls.push(respPartial.loadMore)
                }
                foundItems = [...foundItems, ...respPartial.list]
            }

            setLoadMoreCalls(newLoadMoreCalls)
            setCurrentSearch(search)

            if (isFirstPage) {
                setOptions(foundItems)
            } else {
                setOptions([
                    ...options,
                    ...foundItems
                ])
            }

            const newHasMore = newLoadMoreCalls.length > 0
            setHasMore(newHasMore)

            const preparedItems = foundItems.map(toSelectOption)

            return {
                options: search && isCreatable && isFirstPage ? [{
                    label: t('selfPopulating.existingEntries'),
                    options: preparedItems,
                }] : preparedItems,
                hasMore: newHasMore
            }
        } finally {
            session.notifyLoadEnd(search)
        }
    }

    function handleChange(option: any) {
        if (option.__isNew__) {
            handleAdd(option.value)
            return
        }
        onChange(fromSelectOption(option))
        if (keepValueOnSelect) {
            setValueInternal(option)
        } else {
            clearValue()
        }
    }


    function handleAdd(newOptionValue: string) {
        onAdd(newOptionValue)
        if (keepValueOnSelect) {
            setValueInternal(newOptionValue)
        } else {
            clearValue()
        }
    }

    function handleBlur() {
        closeMenu()
    }

    function onMenuOpen() {
        setIsMenuOpen(true)
        if (shouldShowUncontrolledValue && !(keepValueOnSelect && value && typeof value === 'object')) {
            setValueInternal("")
        }
        handleMenuOpen instanceof Function && handleMenuOpen()
    }

    function onMenuClose() {
        if (suppressMenuCloseRef.current) {
            suppressMenuCloseRef.current = false
            return
        }
        if (forceKeepMenuOpen) {
            return
        }
        closeMenu()
        handleMenuClose instanceof Function && handleMenuClose()
    }

    function closeMenu() {
        if (forceKeepMenuOpen) {
            return
        }
        setIsMenuOpen(false)
        session.handleMenuClosed()
    }


    function suppressControlMenuClose(event: React.SyntheticEvent) {
        if (!isMenuOpen) return
        const target = event.target as Element
        if (!(target instanceof Element)) return
        if (target.closest(".select__menu")) return
        if (target.closest(".select__indicators")) return
        suppressMenuCloseRef.current = true
        window.setTimeout(() => { suppressMenuCloseRef.current = false }, 0)
    }

    function clearValue() {
        setValueInternal(shouldShowUncontrolledValue ? undefined : "")
        setCurrentSearch("")
        setErrorMessage(null)
        session.handleCleared()
        closeMenu()
        onClear?.()
    }

    function formatOptionLabel(option, extra) {

        if (option.__isNew__) {
            return <SelectCreateLabel search={option.value} testId={`${testIdPrefix}-create`} />
        }
        const item = fromSelectOption(option)
        if (shouldShowUncontrolledValue && extra.context === 'value' && valueTemplate) {
            return valueTemplate(item, value)
        }

        if (optionTemplate) {
            return optionTemplate(item, value)
        }
        return (
            <Highlighter text={option.label != null ? option.label.toString() : String(item)} highlight={typeof value === "string" ? value : ""} />
        )
    }

    function handleInputChange(query, { action }) {
        if (action === "set-value" && keepValueOnSelect) {
            return
        }
        if (action !== "input-change") return
        if (keepValueOnSelect && value && typeof value === 'object') {
            onClear?.()
        }
        session.handleTyped()
        setValueInternal(query)
    }

    /**
     * Only typed search text is shown in the input. A selected option (object) yields an empty
     * input so react-select renders the selection as its SingleValue (via formatOptionLabel)
     * instead of as input text: react-select 5 suppresses the SingleValue while inputValue is
     * non-empty AND hides the input itself after a selection, which previously left the selected
     * value invisible until the field was re-focused.
     */
    const displayInputValue = useMemo(() => {
        if (typeof value === 'string') return value
        return ''
    }, [value])
    session.syncInput(displayInputValue)

    function handleKeyDown(e: KeyboardEvent<HTMLElement>) {
        if (e.key !== "Enter") return
        e.stopPropagation()
        session.handleEnter(isMenuOpen)
    }

    const components = useMemo(() => SelectComponents({
        dropdownIndicator,
        onCreateArticleClick,
        testIdPrefix,
        markSelectedOption: false,
    }), [onCreateArticleClick, dropdownIndicator, testIdPrefix])

    const styles = useMemo(() => {
        return getSelectStyles({
            isSelectSearch: true,
            hasTooltip: !!helpTooltip,
            grouped,
            menuWidth,
            menuAlign,
            isInvalid: !!errorMessage || isInvalid,
            isInvalidMenu: !!errorMessage,
            hasSelectSearchValue: hasValue,
            menuZIndex,
            hasMore,
        })
    }, [grouped, menuWidth, menuAlign, helpTooltip, errorMessage, hasValue, menuZIndex, hasMore, isInvalid])

    const props = {
        classNamePrefix: "select",
        debounceTimeout,
        loadOptions: loadOptions,
        components: components,
        onChange: handleChange,
        reduceGroupedOptions,
        formatOptionLabel: formatOptionLabel,
        styles: styles,
        createOptionPosition: "first" as const,
        value,
        inputValue: displayInputValue,
        onCreateOption: handleAdd,
        placeholder: placeholder,
        blurInputOnSelect,
        onMenuOpen,
        onMenuClose,
        menuIsOpen: isMenuOpen,
        onBlur: handleBlur,
        onInputChange: handleInputChange,
        onKeyDown: handleKeyDown,
        // Never put the typed text here: a cacheUniqs change reloads immediately and skips
        // debounceTimeout, which only covers the "input-change" caller (EMTMP-1300).
        cacheUniqs: [...(searchOnlyWithCriteria ? [session.searchEpoch] : []), ...(extraCacheUniqs || [])],
        clearCacheOnSearchChange: true,
        defaultOptions: searchOnlyWithCriteria ? session.hasSearched : defaultOptions,
        loadOptionsOnMenuOpen,
        noOptionsMessage: () => JSON.stringify({
            isError: !!errorMessage,
            message: searchOnlyWithCriteria && !session.hasSearched
                ? t('common.searchTypeOrEnterHint')
                : (errorMessage || t('common.noResultsFound')),
            type: onCreateArticleClick && !(searchOnlyWithCriteria && !session.hasSearched) ? "articleSearch" : undefined
        } satisfies INoOptionsMessageConfigJson),
        menuPortalTarget: isInModal ? rootSelectMenuPortal : undefined,
        menuPlacement: (isInModal ? "auto" : "bottom") as MenuPlacement,
        closeMenuOnScroll: isInModal ? (event) => {
            return !rootSelectMenuPortal.contains(event.target)
        } : false,
        menuPosition: (isInModal ? "fixed" : "absolute") as MenuPosition,
        shouldLoadMore: function (scrollHeight: number, clientHeight: number, scrollTop: number) {
            const bottomBorder = scrollHeight - clientHeight - 100
            return bottomBorder < scrollTop
        },
        isDisabled,
    }

    return (
        <div className="select"
            ref={wrapperRef}
            onMouseDownCapture={suppressControlMenuClose}
            onTouchEndCapture={suppressControlMenuClose}>
            {isCreatable &&
                <CreatableAsyncPaginate
                    {...props}
                    isValidNewOption={(inputValue: string) => !!inputValue && !errorMessage} />
            }
            {!isCreatable &&
                <AsyncPaginate {...props} />
            }
            <div className="select__additional-indicators">
                {hasValue &&
                    <div className="select__clear-indicator" aria-label="clear" onClick={clearValue}>
                        <img src={iconClearIndicator} alt="" />
                    </div>
                }
                {!!helpTooltip &&
                    <div className="select__tooltip">
                        <HelpButton tooltipProps={helpTooltip} />
                    </div>
                }
            </div>
        </div>
    )
}

export default SelectSearch
